import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BIO_MAX, MAX_CLUB_GENRES, type Me, type ProfileCard, type ProfilePatch, type Stamp } from "@pagemates/shared";
import { api, describeError, RequestError } from "../lib/api";
import { pageAt, posLabel, unitAt } from "../lib/format";
import { Avatar, EmptyState, Spinner, useScrollLock, useSheetDrag } from "./ui";
import { CloseIcon, LockIcon, PencilIcon, SettingsIcon } from "./Icons";
import { CardStack, longDate, monthYear } from "./LibraryCard";
import { EditableAvatar } from "./AvatarPicker";
import { GenreChips } from "./ClubHome";
import { Stars } from "./Stars";
import { Saved } from "../pages/club/ClubSettingsPage";
import { account } from "@pagemates/shared/copy";

const txt = account.profile;

/*
 * A person's profile: their library card and the few lines around it
 * (PLAN.md §Profiles as library cards). The server decides what this viewer
 * may see; this file only lays it out. Opened as a sheet (phones) or a pane
 * (desktop) over whatever you were doing, from faces in Talk, the members
 * list and the meeting; your own is a page at /me.
 */

const MORE_READING = 3;

/** "3 clubs", or the one club's name, or nothing: whose stamps these are. */
function clubLine(card: ProfileCard): string | null {
  const names = new Set(card.stamps.map((s) => s.club?.name ?? s.clubName ?? null));
  const known = [...names].filter(Boolean) as string[];
  if (known.length === 0) return null;
  if (names.size === 1) return known[0]!;
  return txt.clubs(names.size);
}

/** `me` makes your own card editable in place (the /me page); without it, a card is read-only. */
export function ProfileBody({ card, me, onStamp, fresh, onFreshDone }:
  { card: ProfileCard; me?: Me; onStamp: (s: Stamp) => void; fresh?: boolean; onFreshDone?: () => void }) {
  const own = card.relation === "self" && Boolean(me);
  const mine = card.relation === "self";
  const [allReading, setAllReading] = useState(false);
  const reading = allReading ? card.reading : card.reading.slice(0, MORE_READING);
  const hidden = card.reading.length - reading.length;
  return (
    <div className="prof">
      <div className="ph">
        {own ? <EditableAvatar me={me!} /> : <Avatar name={card.name} image={card.image} size="lg" />}
        <div className="ph-text">
          {own ? <NameEdit card={card} me={me} /> : <h2 className="nm">{card.name}</h2>}
          {!card.minimal && (own ? <BioEdit value={card.bio} /> : card.bio && <p className="bio">{card.bio}</p>)}
          {!card.minimal ? (
            <p className="meta">
              {card.issuedAt && <span>{txt.cardIssued(monthYear(card.issuedAt))}</span>}
              {card.hardcoverUsername && <><span aria-hidden="true">·</span><a href={`https://hardcover.app/@${card.hardcoverUsername}`} target="_blank" rel="noreferrer">{txt.hardcover}</a></>}
            </p>
          ) : <p className="meta">{txt.keepsTheirCardTo}</p>}
        </div>
      </div>

      {card.reading.length > 0 && (
        <section>
          <div className="sect"><h3>{txt.readingNow}</h3>{card.reading.length > 1 && <span className="count">{card.reading.length}</span>}</div>
          {reading.map((r) => {
            const unit = unitAt(r.percent, { pages: r.pages, audioSeconds: r.audioSeconds, myFormat: r.format ?? undefined });
            return (
              <div key={r.clubBookId} className="now-row">
                <span className="cover" aria-hidden="true">{r.book.coverUrl && <img src={r.book.coverUrl} alt="" loading="lazy" />}</span>
                <div style={{ minWidth: 0 }}>
                  <div className="t">{r.book.title}</div>
                  <div className="d"><b className={mine ? "mine" : undefined}>{r.percent}%</b>{unit ? ` · ${unit}` : ""} · {r.club.name}</div>
                </div>
              </div>
            );
          })}
          {hidden > 0 && <button type="button" className="linkish small now-more" onClick={() => setAllReading(true)}>{txt.andMore(hidden)}</button>}
        </section>
      )}

      {!card.minimal && (own ? <GenresEdit value={card.genres} /> : card.genres.length > 0 && <div className="genre-pills">{card.genres.map((g) => <span key={g} className="pill">{g}</span>)}</div>)}

      {!card.minimal && (
        <CardStack name={card.name} issuedAt={card.issuedAt} clubLine={clubLine(card)} stamps={card.stamps} onStamp={onStamp} fresh={fresh} onFreshDone={onFreshDone} />
      )}

      {own ? <QuoteEdit card={card} /> : card.quote && <PinnedQuote quote={card.quote} />}

      {!card.minimal && <p className="totals"><b>{card.totals.books}</b> {card.totals.books === 1 ? txt.book : txt.books} · <b>{card.totals.pages.toLocaleString()}</b> pages</p>}
    </div>
  );
}

function PinnedQuote({ quote }: { quote: NonNullable<ProfileCard["quote"]> }) {
  const at = posLabel(quote.position, { pages: quote.pages });
  if (quote.sealed) {
    return (
      <div className="pq sealed-q">
        <LockIcon width={14} height={14} aria-hidden="true" />
        <span>{txt.aQuoteFrom}<b>{quote.title}</b>{txt.readPastToSee(quote.pages ? txt.p(pageAt(quote.position, quote.pages)) : `${quote.position}%`)}</span>
      </div>
    );
  }
  return (
    <figure className="pq">
      <blockquote>{quote.body}</blockquote>
      <figcaption>{quote.title} · {at}</figcaption>
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Editing your own card in place: pencil, then Save or Cancel ("✓ Saved").
// ---------------------------------------------------------------------------
function useSaveProfile() {
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const m = useMutation({
    mutationFn: (p: ProfilePatch) => api.updateProfile(p),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ["profile"] }), qc.invalidateQueries({ queryKey: ["me"] })]);
      setSaved(true); window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setSaved(false), 1800);
    },
  });
  return { ...m, saved };
}

function Pencil({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="pencil" aria-label={label} onClick={onClick}><PencilIcon width={15} height={15} /></button>;
}

function TextEdit({ value, max, label, placeholder, onSave, onCancel, busy, allowEmpty = true }:
  { value: string; max: number; label: string; placeholder?: string; onSave: (v: string) => void; onCancel: () => void; busy?: boolean; allowEmpty?: boolean }) {
  const [v, setV] = useState(value);
  const t = v.trim();
  const can = t !== value.trim() && (allowEmpty || t.length > 0);
  return (
    <div className="pedit">
      <input className="input" aria-label={label} value={v} maxLength={max} placeholder={placeholder} autoFocus onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") onCancel(); if (e.key === "Enter" && can) onSave(t); }} />
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn primary sm" disabled={!can || busy} onClick={() => onSave(t)}>{txt.save}</button>
        <button type="button" className="btn ghost sm" onClick={onCancel}>{txt.cancel}</button>
        <span className="muted small" style={{ marginLeft: "auto" }}>{max - v.length}</span>
      </div>
    </div>
  );
}

function NameEdit({ card, me }: { card: ProfileCard; me?: Me }) {
  const [editing, setEditing] = useState(false);
  const save = useSaveProfile();
  if (editing) {
    return <TextEdit label={txt.nameShownInClubs} value={me?.displayName ?? card.name} max={40} placeholder={me?.name} busy={save.isPending}
      onCancel={() => setEditing(false)} onSave={(v) => save.mutate({ displayName: v || null }, { onSuccess: () => setEditing(false) })} />;
  }
  return <h2 className="nm">{card.name}<Pencil label={txt.editYourName} onClick={() => setEditing(true)} /><Saved on={save.saved} /></h2>;
}

function BioEdit({ value }: { value: string | null }) {
  const [editing, setEditing] = useState(false);
  const save = useSaveProfile();
  if (editing) {
    return <TextEdit label={txt.oneLineAboutYou} value={value ?? ""} max={BIO_MAX} placeholder={txt.slowReaderFastTalker} busy={save.isPending}
      onCancel={() => setEditing(false)} onSave={(v) => save.mutate({ bio: v || null }, { onSuccess: () => setEditing(false) })} />;
  }
  return (
    <p className={`bio${value ? "" : " blank"}`}>{value ?? txt.addALineAbout}<Pencil label={txt.editTheLineAbout} onClick={() => setEditing(true)} /><Saved on={save.saved} /></p>
  );
}

function GenresEdit({ value }: { value: string[] }) {
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState(value);
  const save = useSaveProfile();
  useEffect(() => { if (!editing) setPick(value); }, [value, editing]);
  if (editing) {
    return (
      <section className="pedit">
        <div className="sect"><h3>{txt.favouriteGenres}</h3><span className="count">{txt.upTo(MAX_CLUB_GENRES)}</span></div>
        <GenreChips value={pick} onChange={setPick} />
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn primary sm" disabled={save.isPending || pick.join("|") === value.join("|")} onClick={() => save.mutate({ genres: pick }, { onSuccess: () => setEditing(false) })}>{txt.save}</button>
          <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>{txt.cancel}</button>
        </div>
      </section>
    );
  }
  return (
    <div className="row pgenres">
      {value.length ? <div className="genre-pills">{value.map((g) => <span key={g} className="pill">{g}</span>)}</div> : <span className="muted small">{txt.favouriteGenres}</span>}
      <Pencil label={txt.editFavouriteGenres} onClick={() => setEditing(true)} /><Saved on={save.saved} />
    </div>
  );
}

function QuoteEdit({ card }: { card: ProfileCard }) {
  const [editing, setEditing] = useState(false);
  const quotes = useQuery({ queryKey: ["my-quotes"], queryFn: api.myQuotes, enabled: editing });
  const save = useSaveProfile();
  const pinned = card.own?.pinnedQuote ?? null;
  return (
    <section>
      <div className="sect"><h3>{txt.pinnedQuote}<Saved on={save.saved} /></h3>{!editing && <Pencil label={txt.chooseAQuoteTo} onClick={() => setEditing(true)} />}</div>
      {!editing && (card.quote ? <PinnedQuote quote={card.quote} /> : <p className="muted small">{txt.pinAQuoteYou}</p>)}
      {editing && (
        <div className="qpick">
          {quotes.isPending ? <Spinner /> : quotes.data?.length ? quotes.data.map((q) => (
            <button key={`${q.source}:${q.id}`} type="button" className={`vis-opt${q.pinned ? " on" : ""}`} aria-pressed={q.pinned} disabled={save.isPending}
              onClick={() => save.mutate({ pinnedQuote: { source: q.source, id: q.id } }, { onSuccess: () => setEditing(false) })}>
              <span className="radio-dot" aria-hidden="true" />
              <span><b className="qb">{q.body}</b><span className="d">{q.title} · {posLabel(q.position, { pages: q.pages })} · {q.source === "note" ? txt.yourNotes : q.clubName}</span></span>
            </button>
          )) : <p className="muted small">{txt.noQuotesYetPost}</p>}
          <div className="row" style={{ gap: 8 }}>
            {pinned && <button type="button" className="btn ghost sm" disabled={save.isPending} onClick={() => save.mutate({ pinnedQuote: null }, { onSuccess: () => setEditing(false) })}>{txt.unpin}</button>}
            <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>{txt.cancel}</button>
          </div>
          {save.isError && <p className="error small">{describeError(save.error)}</p>}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tapping a stamp: the recap when the viewer may open it, else a small book panel.
// ---------------------------------------------------------------------------
export function useStampTap(owner: { name: string }, after?: () => void) {
  const navigate = useNavigate();
  const [panel, setPanel] = useState<Stamp | null>(null);
  const tap = useCallback((s: Stamp) => {
    if (s.recap && s.club) {
      after?.();
      void navigate({ to: "/clubs/$clubId/recap/$bookId", params: { clubId: s.club.slug ?? s.club.id, bookId: s.id } });
    } else setPanel(s);
  }, [navigate, after]);
  const node = panel ? <StampPanel stamp={panel} ownerName={owner.name} onClose={() => setPanel(null)} /> : null;
  return { tap, node };
}

function StampPanel({ stamp: s, ownerName, onClose }: { stamp: Stamp; ownerName: string; onClose: () => void }) {
  useScrollLock();
  const ref = useRef<HTMLDivElement>(null);
  useSheetDrag(ref, onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const when = s.kind === "dnf" ? txt.returnedUnfinished(longDate(s.at)) : s.kind === "back" ? txt.readIn(new Date(s.at).getFullYear()) : txt.finished(longDate(s.at));
  return (
    <>
      <div className="modal-dim stamp-dim" onClick={onClose} />
      <div className="modal stamp-panel" role="dialog" aria-label={s.book.title} ref={ref}>
        <div className="grab" />
        <button type="button" className="x" aria-label={txt.close} onClick={onClose}><CloseIcon /></button>
        <div className="hd">
          <span className="cover lg" aria-hidden="true">{s.book.coverUrl && <img src={s.book.coverUrl} alt="" />}</span>
          <div style={{ minWidth: 0 }}>
            <div className="t">{s.book.title}</div>
            {s.book.authors.length > 0 && <div className="a">{s.book.authors.join(", ")}</div>}
            <div className="dt">{when}{s.pages ? txt.pages(s.pages) : ""}</div>
          </div>
        </div>
        {s.halfStars && s.kind !== "dnf" ? <div className="rowx"><span>{txt.sRating(ownerName)}</span><Stars value={s.halfStars / 2} size="sm" /></div> : null}
        {s.book.slug && <a className="hc-link" href={`https://hardcover.app/books/${s.book.slug}`} target="_blank" rel="noreferrer">{txt.hardcover}</a>}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Someone's card over the current screen. One at a time, from anywhere.
// ---------------------------------------------------------------------------
const Ctx = createContext<(userId: string) => void>(() => {});
export const useOpenProfile = () => useContext(Ctx);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(null);
  return (
    <Ctx.Provider value={setUserId}>
      {children}
      {userId && <ProfileSheet key={userId} userId={userId} onClose={() => setUserId(null)} />}
    </Ctx.Provider>
  );
}

/** A face that opens its person's card. */
export function ProfileFace({ user, className, children }: { user: { id: string; name: string; image: string | null }; className?: string; children?: ReactNode }) {
  const open = useOpenProfile();
  return (
    <button type="button" className={`face-btn${className ? ` ${className}` : ""}`} aria-label={txt.sCard(user.name)}
      onClick={(e) => { e.stopPropagation(); open(user.id); }}>
      {children ?? <Avatar name={user.name} image={user.image} />}
    </button>
  );
}

function ProfileSheet({ userId, onClose: unmount }: { userId: string; onClose: () => void }) {
  useScrollLock();
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ["profile", userId], queryFn: () => api.profile(userId), retry: false });
  const onClose = useCallback(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { unmount(); return; }
    setClosing(true); window.setTimeout(unmount, 320);
  }, [unmount]);
  useSheetDrag(ref, unmount);
  const { tap, node } = useStampTap({ name: q.data?.name ?? "" }, unmount);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !node) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, node]);
  const self = q.data?.relation === "self";
  return (
    <>
      <div className={`modal-dim book-dim${closing ? " out" : ""}`} onClick={onClose} />
      <div className={`modal side book-panel profile-panel${closing ? " out" : ""}`} role="dialog" aria-label={q.data ? txt.sCard(q.data.name) : txt.profile} ref={ref}
        onAnimationEnd={(e) => { if (closing && e.target === e.currentTarget) unmount(); }}>
        <div className="grab" />
        <div className="pp-top">
          {self && <Link to="/me/settings" className="pencil" aria-label={txt.settings} onClick={unmount}><SettingsIcon width={18} height={18} /></Link>}
          <button type="button" className="x" aria-label={txt.close} onClick={onClose}><CloseIcon /></button>
        </div>
        {q.isPending ? <Spinner /> : q.data ? <ProfileBody card={q.data} onStamp={tap} />
          : <EmptyState title={txt.thisProfileIsnT}>{q.error instanceof RequestError && q.error.status === 404 ? txt.itMayBePrivate : describeError(q.error)}</EmptyState>}
      </div>
      {node}
    </>
  );
}
